import moment from 'moment'
import { formatNumber } from 'utils/formatNumber'

// Builds the "Job Details" key/value map rendered by <DetailTable /> in role
// notification emails. Ported from institute-react's role-creation email so the
// corporate TPO mail shows the exact same table.
//
// `role` is the payload of GET /corporates/jobs/:roleId (the same shape both
// apps read), so every field is optional and simply omitted when absent.

const getCompensationKey = role =>
  role?.jobCategory === 'PLACEMENT' || role?.jobCategory === 'GIGS'
    ? 'CTC'
    : 'Stipend'

const getCompensationValue = role => {
  if (
    !role?.stipend &&
    role?.jobCategory !== 'PLACEMENT' &&
    role?.jobCategory !== 'GIGS'
  ) {
    return 'No Stipend'
  }

  if (
    role?.minCompensation === null ||
    role?.minCompensation === undefined ||
    role?.minCompensation === '' ||
    Number(role?.minCompensation) === 0
  ) {
    return ''
  }

  const formattedMin = formatNumber(Number(role.minCompensation))

  if (
    role?.maxCompensation &&
    Number(role.maxCompensation) > 0 &&
    Number(role.maxCompensation) !== Number(role.minCompensation)
  ) {
    return `Annually - ₹ ${formattedMin} - ₹ ${formatNumber(
      Number(role.maxCompensation)
    )}`
  }

  return `Annually - ₹ ${formattedMin}`
}

const getExperienceText = role => {
  if (
    role?.jobTypeLevels?.[0] === 'ENTRY' ||
    role?.jobTypeLevels?.[0] === 'INTERN'
  ) {
    return 'Fresher'
  }

  // yearOfExperience holds "years.months" as a number, e.g. 2.6 => 2 yrs 6 mo.
  const split = value => {
    const [year = '0', month = '0'] = String(value ?? '').split('.')
    return { year, month }
  }

  const min = split(role?.yearOfExperience?.[0])
  let text = `${Number(min.year)}${Number(min.year) <= 1 ? ' year' : ' years'}`
  if (Number(min.month) > 0) text += ` ${min.month} months`

  if (role?.yearOfExperience?.[1]) {
    const max = split(role.yearOfExperience[1])
    text += ` - ${Number(max.year)}${Number(max.year) <= 1 ? ' year' : ' years'}`
    if (Number(max.month) > 0) text += ` ${max.month} months`
  }

  return text
}

const getEligibleQualification = role => {
  const qualificationParts = []

  role.eligibilityCriteria.forEach(criteria => {
    if (!criteria.selected) return

    const qualTypes = []
    Object.entries(criteria.selected).forEach(([qualType, degrees]) => {
      if (!degrees?.length) return

      // "Any UG" style selection wins over the individual degrees under it.
      if (degrees.some(degree => degree.isAllDegree)) {
        qualTypes.push(`Any ${qualType}`)
        return
      }

      const degreeList = []
      degrees.forEach(degree => {
        if (!degree.streams?.length) {
          degreeList.push(degree.shortForm)
          return
        }
        degree.streams.forEach(stream => {
          if (stream.specialisations?.length) {
            stream.specialisations.forEach(spec => {
              degreeList.push(
                `${degree.shortForm} - ${stream.name} - ${spec.name}`
              )
            })
          } else {
            degreeList.push(`${degree.shortForm} - ${stream.name}`)
          }
        })
      })

      if (degreeList.length) qualTypes.push(degreeList.join(' Or '))
    })

    if (qualTypes.length) qualificationParts.push(qualTypes.join(' Or '))
  })

  // "(And)" is the separator DetailTable splits on to render multi-row criteria.
  return qualificationParts.length ? qualificationParts.join('(And)') : ''
}

const getEligibleScore = role => {
  const scores = [
    role?.tenthScore && role.tenthScore !== '0'
      ? `10th - ${role.tenthScore}%`
      : null,
    role?.twelfthScore && role.twelfthScore !== '0'
      ? `12th - ${role.twelfthScore}%`
      : null,
    role?.UGScore && role.UGScore !== '0' ? `UG - ${role.UGScore}%` : null,
    role?.PGScore && role.PGScore !== '0' ? `PG - ${role.PGScore}%` : null,
    role?.UGDiplomaScore && role.UGDiplomaScore !== '0'
      ? `Diploma - ${role.UGDiplomaScore}%`
      : null,
  ].filter(Boolean)

  return scores.join(', ')
}

const addSkillRows = (data, role) => {
  const primary = role?.mandatorySkillNames?.primarySkillNames
  const mandatory = role?.mandatorySkillNames?.mandatorySkillNames
  const mandatoryCount = role?.mandatorySkillNames?.mandatoryCount || 1

  const joinPrimary = () => {
    const separator =
      primary.type === 'OR' ? ' Or ' : primary.type === 'AND' ? ' And ' : ', '
    return primary.skillNames.map(skill => skill.name).join(separator)
  }
  const joinMandatory = () =>
    `${mandatory
      .map(skill => skill.name)
      .join(', ')} (Any ${mandatoryCount} skill from this set is mandatory)`

  if (primary?.skillNames?.length && mandatory?.length) {
    data['Skills Required (Set 1)'] = joinPrimary()
    data['Skills Required (Set 2)'] = joinMandatory()
  } else if (primary?.skillNames?.length) {
    data['Skills Required'] = joinPrimary()
  } else if (mandatory?.length) {
    data['Skills Required'] = joinMandatory()
  }
}

// Deadline shown both inside the table and in the email body, so both read the
// same instant. endDate is a true instant; formatted in UTC to match the rest
// of the role-notification emails.
export const formatApplicationDeadline = endDate =>
  endDate ? moment.utc(endDate).format('DD-MM-YYYY hh:mm A') : ''

export const buildJobDetailsTable = (role = {}) => {
  const data = {}

  if (role?.company?.companyName) data['Company Name'] = role.company.companyName
  if (role?.company?.website) data['Company Website'] = role.company.website
  if (role?.company?.description) {
    data['About the company'] = role.company.description.replace(/<[^>]*>/g, '')
  }
  if (role?.tittle) data['Job role'] = role.tittle
  if (role?.jobCategory) {
    data['Job type'] =
      role.jobCategory.charAt(0).toUpperCase() +
      role.jobCategory.slice(1).toLowerCase()
  }
  if (role?.yearOfExperience?.[0] || role?.yearOfExperience?.[0] === 0) {
    data['Experience'] = getExperienceText(role)
  }

  const compensation = getCompensationValue(role)
  if (compensation) data[getCompensationKey(role)] = compensation

  if (role?.cities?.length) {
    data['Job location'] = role.cities
      .map(city => city.name || city?.state_name)
      .join(', ')
  } else if (role?.isAnyWhere) {
    data['Job location'] = 'Anywhere'
  }

  if (role?.eligibilityCriteria?.length) {
    const qualification = getEligibleQualification(role)
    if (qualification) data['Eligible Qualification'] = qualification
  }

  if (role?.yearOfPassing?.length) {
    data['Year of Passing'] = role.yearOfPassing.join(', ')
  }

  const eligibleScore = getEligibleScore(role)
  if (eligibleScore) data['Eligible score'] = eligibleScore

  addSkillRows(data, role)

  if (role?.startDate) {
    data['Application opens from'] = moment(role.startDate).format('DD-MM-YYYY')
  }
  const deadline = formatApplicationDeadline(role?.endDate)
  if (deadline) data['Application closes at'] = deadline

  return data
}
